import React, { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Building2, Wallet, Users2 } from "lucide-react";
import { toast } from "sonner";

export default function Franchise() {
  const [partners, setPartners] = useState([]);
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    const [p, s] = await Promise.all([api.get("/franchise/partners"), api.get("/franchise/summary")]);
    setPartners(p.data); setSummary(s.data);
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this partner?")) return;
    await api.delete(`/franchise/partners/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <AppLayout title="Franchise">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-5">
        <StatCard icon={Building2} label="Active partners"    value={summary?.total_partners} accent="#041B46"/>
        <StatCard icon={Users2}    label="Leads referred"     value={summary?.total_leads_referred} accent="#0EA5FF"/>
        <StatCard icon={Wallet}    label="Commission due"     value={summary ? `₹${(summary.total_commission_due || 0).toLocaleString("en-IN")}` : "—"} accent="#5FBF3A"/>
      </div>

      <div className="flex justify-end mb-3">
        <NewPartnerDialog open={open} setOpen={setOpen} onCreated={load}/>
      </div>

      <div className="bg-white border border-[var(--miro-border)] rounded-xl overflow-x-auto">
        <table data-testid="franchise-table" className="w-full text-sm">
          <thead className="bg-[var(--miro-bg)] text-[var(--miro-text-secondary)]">
            <tr className="text-left">
              <th className="px-4 py-3 font-semibold">Partner</th>
              <th className="px-4 py-3 font-semibold">City</th>
              <th className="px-4 py-3 font-semibold">Commission %</th>
              <th className="px-4 py-3 font-semibold text-right">Leads</th>
              <th className="px-4 py-3 font-semibold text-right">Won revenue</th>
              <th className="px-4 py-3 font-semibold text-right">Commission due</th>
              <th className="px-4 py-3"/>
            </tr>
          </thead>
          <tbody>
            {partners.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-[var(--miro-text-muted)]">No franchise partners yet.</td></tr>
            )}
            {partners.map(p => {
              const row = (summary?.partners || []).find(r => r.partner_id === p.id) || {};
              return (
                <tr key={p.id} data-testid={`franchise-row-${p.id}`} className="border-t border-[var(--miro-border)]">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-[var(--miro-navy)]">{p.name}</div>
                    <div className="text-xs text-[var(--miro-text-muted)]">{p.contact_email}</div>
                  </td>
                  <td className="px-4 py-3 text-[var(--miro-text-secondary)]">{p.city}</td>
                  <td className="px-4 py-3 font-mono text-[var(--miro-navy)]">{p.commission_rate}%</td>
                  <td className="px-4 py-3 text-right font-mono text-[var(--miro-navy)]">{row.leads_referred || 0}</td>
                  <td className="px-4 py-3 text-right font-mono text-[var(--miro-navy)]">₹{(row.won_revenue || 0).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold text-[var(--miro-green-600)]">₹{(row.commission_due || 0).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3 text-right">
                    <button data-testid={`franchise-delete-${p.id}`} onClick={() => remove(p.id)} className="text-[var(--miro-text-muted)] hover:text-red-600">
                      <Trash2 size={15}/>
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </AppLayout>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="miro-card p-5">
      <div className="flex items-start justify-between">
        <div className="miro-overline">{label}</div>
        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${accent}18`, color: accent }}>
          <Icon size={17}/>
        </div>
      </div>
      <div className="font-display font-black text-2xl mt-3 text-[var(--miro-navy)]">{value ?? "—"}</div>
    </div>
  );
}

function NewPartnerDialog({ open, setOpen, onCreated }) {
  const [f, setF] = useState({ name: "", city: "", contact_email: "", contact_phone: "", commission_rate: 10, active: true, notes: "" });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setF(x => ({ ...x, [k]: v }));

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/franchise/partners", { ...f, commission_rate: Number(f.commission_rate) || 0 });
      toast.success("Partner added");
      setOpen(false);
      setF({ name: "", city: "", contact_email: "", contact_phone: "", commission_rate: 10, active: true, notes: "" });
      onCreated?.();
    } catch (e) { toast.error("Create failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-franchise-btn" style={{ background: "var(--miro-navy)" }}><Plus size={16} className="mr-1"/> New partner</Button>
      </DialogTrigger>
      <DialogContent className="bg-white">
        <DialogHeader><DialogTitle className="font-display text-2xl text-[var(--miro-navy)]">New franchise partner</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Name *</Label><Input data-testid="new-franchise-name" value={f.name} onChange={e=>upd("name",e.target.value)} className="mt-1"/></div>
          <div><Label>City *</Label><Input data-testid="new-franchise-city" value={f.city} onChange={e=>upd("city",e.target.value)} className="mt-1"/></div>
          <div><Label>Commission %</Label><Input data-testid="new-franchise-comm" type="number" value={f.commission_rate} onChange={e=>upd("commission_rate",e.target.value)} className="mt-1"/></div>
          <div><Label>Contact email *</Label><Input data-testid="new-franchise-email" type="email" value={f.contact_email} onChange={e=>upd("contact_email",e.target.value)} className="mt-1"/></div>
          <div><Label>Contact phone</Label><Input data-testid="new-franchise-phone" value={f.contact_phone} onChange={e=>upd("contact_phone",e.target.value)} className="mt-1"/></div>
          <div className="col-span-2"><Label>Notes</Label><Textarea data-testid="new-franchise-notes" rows={2} value={f.notes} onChange={e=>upd("notes",e.target.value)} className="mt-1"/></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
          <Button data-testid="new-franchise-submit" disabled={!f.name.trim()||!f.city.trim()||!f.contact_email.trim()||saving} onClick={submit} style={{ background: "var(--miro-navy)" }}>
            {saving ? "Saving…" : "Add partner"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
